import React, { useMemo, useRef, useState } from "react";
import TimelineControls from "./timeline/TimelineControls";
import TimelineLegend from "./timeline/TimelineLegend";
import TimelineSvg from "./timeline/TimelineSvg";
import TimelineTooltip from "./timeline/TimelineTooltip";
import TimelineZoomControls from "./timeline/TimelineZoomControls";
import { buildLayerCounts, buildPeriodMap, buildTimelineView } from "./timeline/selectors";
import { useTimelineState } from "./timeline/state";
import { useTimelineTooltip } from "./timeline/useTimelineTooltip";

const CAPTION = "The biblical layer expands the scale into antiquity. The Egyptian layer is limited to ten human figures and excludes deities; the Muslim and Islamicate layer pairs ten major figures with its researched texts and transmission events. Mythological, legendary and pseudepigraphic identities elsewhere are named explicitly. Selected entries are interspersed by date. Period colour appears as a row band and dot so layer colour and entry shape remain readable.";

function tooltipResetKey(state) {
  return [
    state.mainstreamFigures,
    state.allFigures,
    state.events,
    state.biblical,
    state.islamic,
    state.egyptian
  ].join(":");
}

export default function HistoryTimeline({ timeline }) {
  const chartRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const { state, setLayer } = useTimelineState(timeline.initial);
  const counts = useMemo(() => buildLayerCounts(timeline), [timeline]);
  const periodMap = useMemo(() => buildPeriodMap(timeline.periods), [timeline.periods]);
  const view = useMemo(() => buildTimelineView(timeline, state), [state, timeline]);
  const { clear, handlersFor, tooltip } = useTimelineTooltip(chartRef, tooltipResetKey(state));

  return (
    <figure
      className="history-chart"
      ref={chartRef}
      onKeyDown={(event) => event.key === "Escape" && clear()}
      onPointerDown={(event) => {
        if (!event.target.closest(".history-chart__item-link")) clear();
      }}
    >
      <TimelineControls counts={counts} onToggle={setLayer} state={state} summary={view.summary} />
      <TimelineLegend periodMap={periodMap} state={state} visiblePeriods={view.visiblePeriods} />
      <TimelineZoomControls onChange={setZoom} zoom={zoom} />
      <TimelineSvg handlersFor={handlersFor} periodMap={periodMap} state={state} view={view} zoom={zoom} />
      <TimelineTooltip tooltip={tooltip} />
      <figcaption>{CAPTION}</figcaption>
    </figure>
  );
}
